import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Download, LogOut, RefreshCw, Inbox } from "lucide-react";
import { authedRequest } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const STATUS_STYLES = {
  pending: "border-amber-500/40 text-amber-300 bg-amber-500/10",
  reviewed: "border-sky-500/40 text-sky-300 bg-sky-500/10",
  escalated: "border-red-500/40 text-red-300 bg-red-500/10",
};

const NEXT_STATUS = { pending: "reviewed", reviewed: "escalated", escalated: "pending" };

const selectCls =
  "h-10 rounded-md border border-[#282E3F] bg-[#12151E] px-3 text-sm text-slate-100 focus:outline-none focus:ring-2 focus:ring-sky-500/60";

const AdminPanel = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [division, setDivision] = useState("");
  const [status, setStatus] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (division) params.division = division;
      if (status) params.status = status;
      const { data } = await authedRequest({ url: "/enquiries", params });
      setEnquiries(data);
    } catch {
      toast.error("Could not load enquiries. Please sign in again.");
    } finally {
      setLoading(false);
    }
  }, [division, status]);

  useEffect(() => {
    load();
  }, [load]);

  const cycleStatus = async (enquiry) => {
    const next = NEXT_STATUS[enquiry.status] || "pending";
    try {
      await authedRequest({ url: `/enquiries/${enquiry.id}`, method: "PATCH", data: { status: next } });
      setEnquiries((list) => list.map((e) => (e.id === enquiry.id ? { ...e, status: next } : e)));
      toast.success(`Marked as ${next}`);
    } catch {
      toast.error("Status update failed");
    }
  };

  const exportCsv = async () => {
    try {
      const res = await authedRequest({ url: "/enquiries/export", responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "crestmont_enquiries.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Export failed");
    }
  };

  const signOut = async () => {
    await logout();
    navigate("/admin/login");
  };

  return (
    <div className="min-h-screen pt-28 pb-20" data-testid="admin-panel">
      <div className="max-w-7xl mx-auto px-6 sm:px-12">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-sky-400/90">Crestmont Admin Portal</p>
            <h1 className="mt-2 font-display font-semibold tracking-tight text-2xl sm:text-3xl">Institutional Enquiries</h1>
            <p className="mt-1 text-sm text-slate-400" data-testid="admin-user-label">Signed in as {user?.email}</p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={load}
              data-testid="admin-refresh-btn"
              className="inline-flex items-center gap-2 text-sm border border-[#282E3F] rounded-full px-4 py-2 text-slate-300 hover:border-sky-500/50 hover:text-sky-300 transition-colors"
            >
              <RefreshCw size={14} /> Refresh
            </button>
            <button
              onClick={exportCsv}
              data-testid="admin-export-btn"
              className="inline-flex items-center gap-2 text-sm border border-[#282E3F] rounded-full px-4 py-2 text-slate-300 hover:border-sky-500/50 hover:text-sky-300 transition-colors"
            >
              <Download size={14} /> Export CSV
            </button>
            <button
              onClick={signOut}
              data-testid="admin-logout-btn"
              className="inline-flex items-center gap-2 text-sm rounded-full px-4 py-2 bg-[#181C27] border border-[#282E3F] text-slate-200 hover:text-red-300 hover:border-red-500/40 transition-colors"
            >
              <LogOut size={14} /> Sign Out
            </button>
          </div>
        </div>

        <div className="mt-8 flex flex-wrap gap-4">
          <select value={division} onChange={(e) => setDivision(e.target.value)} className={selectCls} data-testid="filter-division-select">
            <option value="">All Divisions</option>
            <option value="infrastructure">Strategic Infrastructure</option>
            <option value="technology">Advanced Intelligence</option>
            <option value="general">General</option>
          </select>
          <select value={status} onChange={(e) => setStatus(e.target.value)} className={selectCls} data-testid="filter-status-select">
            <option value="">All Statuses</option>
            <option value="pending">Pending</option>
            <option value="reviewed">Reviewed</option>
            <option value="escalated">Escalated</option>
          </select>
        </div>

        <div className="mt-8 space-y-4" data-testid="enquiry-list">
          {loading ? (
            <p className="text-sm text-slate-500 font-mono tracking-wider py-16 text-center">LOADING ENQUIRIES…</p>
          ) : enquiries.length === 0 ? (
            <div className="card-institutional flex flex-col items-center py-16 text-center">
              <Inbox size={32} className="text-slate-500" />
              <p className="mt-4 text-sm text-slate-400" data-testid="enquiry-empty">No enquiries match the current filters.</p>
            </div>
          ) : (
            enquiries.map((e) => (
              <article key={e.id} className="card-institutional" data-testid={`enquiry-card-${e.id}`}>
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <h3 className="font-display font-medium text-lg">{e.name}</h3>
                    <p className="mt-1 text-sm text-slate-400">
                      {e.email}
                      {e.phone ? ` · ${e.phone}` : ""}
                    </p>
                  </div>
                  <button
                    onClick={() => cycleStatus(e)}
                    data-testid={`enquiry-status-${e.id}`}
                    title="Click to advance status"
                    className={`font-mono text-[10px] uppercase tracking-[0.2em] px-3 py-1.5 rounded-full border transition-transform hover:scale-105 ${STATUS_STYLES[e.status] || STATUS_STYLES.pending}`}
                  >
                    {e.status}
                  </button>
                </div>
                <p className="mt-4 text-sm text-slate-300 leading-relaxed whitespace-pre-wrap">{e.message}</p>
                <div className="mt-5 pt-4 border-t border-[#282E3F]/70 flex flex-wrap gap-x-6 gap-y-1 font-mono text-[10px] tracking-[0.18em] text-slate-500 uppercase">
                  <span>Division — {e.division}</span>
                  <span>Type — {e.inquiry_type.replace(/_/g, " ")}</span>
                  <span>{new Date(e.created_at).toLocaleString("en-IN")}</span>
                </div>
              </article>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminPanel;
